import React, { useState, useEffect } from 'react';
import { Save, PhoneCall } from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import FormModalLayout from '../Common/FormModalLayout';
import { ensureRichTextValue, RichTextEditor } from '../RichTextEditor';
import Can from '../Common/Can';
import { P } from '../../constants/permissions';

const ContactModal = ({ contact, onClose, onSave, personOptions = [] }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { register, control, handleSubmit, formState: { errors }, setValue, reset } = useForm();

  const isEditing = !!contact;

  useEffect(() => {
    if (contact) {
      setValue('personId', contact.personId);
      setValue('contactType', contact.contactType);
      setValue('value', contact.value);
      setValue('description', ensureRichTextValue(contact.description || ''));
      setValue('isActive', contact.isActive === true || contact.isActive === 'true' ? 'true' : 'false');
    } else {
      reset({
        personId: '',
        contactType: '',
        value: '',
        description: '',
        isActive: 'true',
      });
    }
  }, [contact, setValue, reset]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      await onSave(data);
    } catch (error) {
      console.error('Error saving contact:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormModalLayout
      title={isEditing ? 'Editar contato' : 'Novo contato'}
      subtitle="Vincule telefone ou e-mail a uma pessoa cadastrada."
      headerIcon={PhoneCall}
      onClose={onClose}
      size="lg"
      historyEntityType="contacts"
      historyEntityId={contact?.id}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-outline" disabled={isLoading}>
            Cancelar
          </button>
          <Can permission={P.CONTACT_ACCESS}>
            <button
              type="submit"
              form="contact-modal-form"
              className="btn btn-primary inline-flex items-center justify-center gap-2"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-b-transparent" />
              ) : (
                <Save className="h-4 w-4 shrink-0" aria-hidden />
              )}
              {isEditing ? 'Atualizar' : 'Salvar'} contato
            </button>
          </Can>
        </>
      }
    >
      <form id="contact-modal-form" onSubmit={handleSubmit(onSubmit)} className="form-stack">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="form-group">
            <label className="form-label form-label-required">Pessoa</label>
            <select className="form-select" {...register('personId', { required: 'Pessoa é obrigatória' })}>
              <option value="">Selecione uma pessoa</option>
              {personOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            {errors.personId && <p className="form-error">{errors.personId.message}</p>}
          </div>

          <div className="form-group">
            <label className="form-label form-label-required">Tipo de contato</label>
            <select
              className="form-select"
              {...register('contactType', { required: 'Tipo de contato é obrigatório' })}
            >
              <option value="">Selecione o tipo</option>
              <option value="email">E-mail</option>
              <option value="phone">Telefone</option>
              <option value="whatsapp">WhatsApp</option>
            </select>
            {errors.contactType && <p className="form-error">{errors.contactType.message}</p>}
          </div>

          <div className="form-group md:col-span-2">
            <label className="form-label form-label-required">Valor</label>
            <input
              type="text"
              className="form-input"
              placeholder="Ex: joao@email.com ou (11) 99999-9999"
              {...register('value', { required: 'Valor é obrigatório' })}
            />
            {errors.value && <p className="form-error">{errors.value.message}</p>}
          </div>

          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="form-select max-w-xs" {...register('isActive')}>
              <option value="true">Ativo</option>
              <option value="false">Inativo</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Descrição</label>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <RichTextEditor
                variant="compact"
                value={field.value || ''}
                onChange={field.onChange}
                placeholder="Descrição do contato (opcional)..."
              />
            )}
          />
        </div>
      </form>
    </FormModalLayout>
  );
};

export default ContactModal;
